import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';
import { Empty } from '@/components/investigation/sections';

/** Tone of a timeline dot; maps to the semantic status colours. */
export type TimelineTone = 'ok' | 'warn' | 'error' | 'info' | 'brand';

export interface TimelineEntry {
  key?: string | number;
  /** Main line (event label / actor + action). */
  title: ReactNode;
  /** Optional secondary line. */
  detail?: ReactNode;
  /** Optional meta line (usually the relative timestamp). */
  meta?: ReactNode;
  tone?: TimelineTone;
}

const DOT: Record<TimelineTone, string> = {
  ok: 'bg-success', warn: 'bg-warning', error: 'bg-danger', info: 'bg-info', brand: 'bg-brand',
};

/**
 * The shared dot-timeline used by every investigation surface (delivery
 * timelines, audit trails, scan histories, evaluation paths). Previously this
 * markup was hand-rolled in ten components; they now all render through here so
 * the affordance stays identical everywhere.
 */
export function Timeline({ entries, emptyLabel = 'None recorded.' }: {
  entries: TimelineEntry[];
  emptyLabel?: string;
}) {
  if (entries.length === 0) return <Empty label={emptyLabel} />;
  return (
    <ol className="relative space-y-3 border-l border-border pl-4">
      {entries.map((e, i) => (
        <li key={e.key ?? i} className="relative">
          <span
            className={cn('absolute -left-[21px] top-1 h-2 w-2 rounded-full ring-4 ring-surface', DOT[e.tone ?? 'brand'])}
            aria-hidden="true"
          />
          <div className="text-sm text-fg">{e.title}</div>
          {e.detail != null && <div className="text-xs text-fg-muted">{e.detail}</div>}
          {e.meta != null && <div className="text-xs text-fg-subtle">{e.meta}</div>}
        </li>
      ))}
    </ol>
  );
}
